import { useState, useCallback } from 'react'
import {
    ChatMessage,
    ChatRequest,
    ChatResponse,
    Conversation,
    ConversationUpdate,
    ConversationListResponse,
} from '../types/copilot'

const API_BASE_URL = '/api/v1/copilot'

export function useChat() {
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [sessionId, setSessionId] = useState<string | null>(null)
    const [conversationId, setConversationId] = useState<number | null>(null)
    const [lastCost, setLastCost] = useState<number | null>(null)
    const [conversations, setConversations] = useState<Conversation[]>([])

    const apiRequest = useCallback(async (url: string, options: RequestInit = {}) => {
        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        const headers = {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': token || '',
            ...options.headers,
        }

        const response = await fetch(url, {
            ...options,
            headers,
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        return response.json()
    }, [])

    const fetchConversations = useCallback(async (): Promise<void> => {
        try {
            const data: ConversationListResponse = await apiRequest(`${API_BASE_URL}/conversations`)
            setConversations(data.conversations)
        } catch (error) {
            console.error('Error fetching conversations:', error)
        }
    }, [apiRequest])

    const sendMessage = useCallback(
        async (content: string) => {
            const userMessage: ChatMessage = { role: 'user', content }
            setMessages((prev) => [...prev, userMessage])
            setIsLoading(true)

            try {
                const request: ChatRequest = {
                    messages: [userMessage],
                    session_id: sessionId || undefined,
                    conversation_id: conversationId || undefined,
                }

                const data: ChatResponse = await apiRequest(`${API_BASE_URL}/chat`, {
                    method: 'POST',
                    body: JSON.stringify(request),
                })

                const assistantMessage: ChatMessage = {
                    role: 'assistant',
                    content: data.choices[0].message.content,
                }

                setMessages((prev) => [...prev, assistantMessage])
                setSessionId(data.session_id)

                // If we got a new conversation_id, refresh the conversation list
                if (data.conversation_id && data.conversation_id !== conversationId) {
                    setConversationId(data.conversation_id)
                    fetchConversations()
                } else {
                    setConversationId(data.conversation_id || null)
                }

                setLastCost(data.cost)
            } catch (error) {
                console.error('Error sending message:', error)
                const errorMessage: ChatMessage = {
                    role: 'assistant',
                    content: 'Sorry, there was an error processing your message. Please try again.',
                }
                setMessages((prev) => [...prev, errorMessage])
            } finally {
                setIsLoading(false)
            }
        },
        [sessionId, conversationId, apiRequest, fetchConversations],
    )

    const clearChat = useCallback(() => {
        setMessages([])
        setSessionId(null)
        setConversationId(null)
        setLastCost(null)
    }, [])

    const createNewConversation = useCallback(async (): Promise<number | null> => {
        try {
            const data = await apiRequest(`${API_BASE_URL}/conversations`, {
                method: 'POST',
                body: JSON.stringify({}),
            })

            const newConversationId = data.conversation_id

            clearChat()
            setConversationId(newConversationId)
            fetchConversations()

            return newConversationId
        } catch (error) {
            console.error('Error creating conversation:', error)
            return null
        }
    }, [clearChat, fetchConversations, apiRequest])

    const loadConversation = useCallback(
        async (conversationId: number) => {
            try {
                const data = await apiRequest(
                    `${API_BASE_URL}/conversations/${conversationId}/messages`,
                )
                setMessages(data.messages)
                setConversationId(conversationId)
                setSessionId(null)
                setLastCost(null)
            } catch (error) {
                console.error('Error loading conversation:', error)
            }
        },
        [apiRequest],
    )

    const updateConversation = useCallback(
        async (conversationId: number, update: ConversationUpdate): Promise<boolean> => {
            try {
                await apiRequest(`${API_BASE_URL}/conversations/${conversationId}`, {
                    method: 'PUT',
                    body: JSON.stringify(update),
                })

                fetchConversations()
                return true
            } catch (error) {
                console.error('Error updating conversation:', error)
                return false
            }
        },
        [fetchConversations, apiRequest],
    )

    const deleteConversation = useCallback(
        async (conversationId: number): Promise<boolean> => {
            try {
                await apiRequest(`${API_BASE_URL}/conversations/${conversationId}`, {
                    method: 'DELETE',
                })

                if (conversationId === conversationId) {
                    clearChat()
                }

                fetchConversations()
                return true
            } catch (error) {
                console.error('Error deleting conversation:', error)
                return false
            }
        },
        [conversationId, clearChat, fetchConversations, apiRequest],
    )

    return {
        messages,
        isLoading,
        sessionId,
        conversationId,
        lastCost,
        conversations,
        sendMessage,
        clearChat,
        createNewConversation,
        loadConversation,
        fetchConversations,
        updateConversation,
        deleteConversation,
    }
}
